import React, { useMemo } from 'react';
import { MainMetric, CustomChartTooltipContent } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { CoinIcon } from '@revfluence/fresh-icons/solid/esm';
import { Bar, BarChart, XAxis } from 'recharts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartLegend,
  ChartLegendContent,
} from '@/shadcn/components/ui/chart';
import { ProjectWidgetContainer } from '../common/ProjectWidgetContainer';

const chartConfig = {
  briefs: {
    label: 'Spend in Briefs',
    color: '#1E4945',
  },
  commissions: {
    label: 'Spend on Commission',
    color: '#2A5A54',
  },
  others: {
    label: 'Others',
    color: '#366C63',
  },
} as ChartConfig;

interface TotalSpentCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  budgetData: any;
  isLoading: boolean;
}

export const TotalSpentCard = ({ budgetData, isLoading }: TotalSpentCardProps) => {
  const chartData = useMemo(
    () => [
      {
        source: 'Spend in Briefs',
        value: budgetData?.spentBudget?.spentInBriefs || 0,
        fill: '#1E4945',
      },
      {
        source: 'Spend on Commission',
        value: budgetData?.spentBudget?.spentOnCommission || 0,
        fill: '#2A5A54',
      },
      {
        source: 'Others',
        value: budgetData?.spentBudget?.spentOnOthers || 0,
        fill: '#366C63',
      },
    ],
    [budgetData],
  );

  const showEmptyState = useMemo(() => {
    if (
      !budgetData?.spentBudget?.spentInBriefs
      && !budgetData?.spentBudget?.spentOnCommission
      && !budgetData?.spentBudget?.spentOnOthers
    ) {
      return true;
    }
    return false;
  }, [budgetData]);

  if (isLoading) {
    return (
      <ProjectWidgetContainer
        title="Total Spent in Budget"
        icon={<CoinIcon className="w-4 h-4" />}
        tooltip="Total amount spent from the budget"
        className="!w-full h-full"
      >
        <div className="w-full h-full flex flex-col justify-center items-center">
          <Skeleton className="h-6 w-full mb-4" />
          <Skeleton className="h-6 w-full mb-4" />
          <Skeleton className="h-4 w-full" />
        </div>
      </ProjectWidgetContainer>
    );
  }

  return (
    <ProjectWidgetContainer
      title="Total Spent in Budget"
      icon={<CoinIcon className="w-4 h-4" />}
      tooltip="Total amount spent from the budget"
      className="!w-full h-full"
    >
      <div className="w-full h-full flex flex-col justify-between">
        <MainMetric
          className="flex-1 w-full"
          value={budgetData?.spentBudget?.spentAmount || 0}
          size="small"
          metricType="currency"
          subHeading="of Total Budget"
          icon={(
            <Badge variant="outline" className="text-grey-4">
              {`${budgetData?.spentBudget?.spentPercent || 0}%`}
            </Badge>
          )}
        />
        {!showEmptyState && (
          <ChartContainer config={chartConfig} className="h-[150px] w-full">
            <BarChart data={chartData} margin={{ top: 0, left: 0 }}>
              <XAxis dataKey="source" tickLine={false} tickMargin={10} axisLine={false} />
              <ChartTooltip cursor={false} content={<CustomChartTooltipContent valueType="currency" />} />
              <Bar dataKey="value" strokeWidth={1} radius={4} barSize={35} />
              <ChartLegend content={<ChartLegendContent />} />
            </BarChart>
          </ChartContainer>
        )}
      </div>
    </ProjectWidgetContainer>
  );
};
